/**
 * Generic cache for API responses. Persists to localStorage so the app can
 * show last-known data immediately on load while refetching in the background.
 * On request error, clear the cached value so stale data is not shown.
 */

const STORAGE_PREFIX = 'sg-apiCache-';

function getStorageKey(cacheKey: string): string {
  return `${STORAGE_PREFIX}${cacheKey}`;
}

export function getStartedTrainingCacheKey(trainingId: string): string {
  return `trainings/${trainingId}/started`;
}

export function clearAllStartedTrainingCaches(): void {
  const prefix = `${STORAGE_PREFIX}trainings/`;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix) && key.endsWith('/started')) {
      localStorage.removeItem(key);
    }
  }
}

export const API_CACHE_KEYS = {
  ME: 'users/me',
  ME_DETAILED: 'users/me/detailed',
  AVAILABLE_EXERCISES: 'exercises/available',
  AVAILABLE_DETAILED_EXERCISES: 'exercises/available-detailed',
  EXERCISE_OPTIONS: 'exercises/options',
} as const;

export type ApiCacheKey = (typeof API_CACHE_KEYS)[keyof typeof API_CACHE_KEYS];

export const getTrainingExercisesCacheKey = (trainingId: string) =>
  `trainings/${trainingId}/exercises`;

export function clearAllTrainingExercisesCaches(): void {
  const prefix = `${STORAGE_PREFIX}trainings/`;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix) && key.endsWith('/exercises')) {
      localStorage.removeItem(key);
    }
  }
}

export const ApiResponseCache = {
  get<T>(cacheKey: string): T | null {
    try {
      const raw = localStorage.getItem(getStorageKey(cacheKey));
      if (raw == null) return null;
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  },

  set<T>(cacheKey: string, data: T): void {
    try {
      localStorage.setItem(
        getStorageKey(cacheKey),
        typeof data === 'string' ? data : JSON.stringify(data),
      );
    } catch {
      // ignore quota / serialization errors
    }
  },

  remove(cacheKey: string): void {
    try {
      localStorage.removeItem(getStorageKey(cacheKey));
    } catch {
      // ignore
    }
  },
};
